<script lang="ts" setup>
import { useI18n } from 'vue-i18n';

import { OTHER_REASON, useCancelTaskDrawer } from '@/composables/useCancelTaskDrawer';

const { t } = useI18n();

const {
  isDrawerOpen,
  subtitle,
  reasonOptions,
  selectedReason,
  customReason,
  isLoading,
  isOtherReason,
  canConfirm,
  closeDialog,
  handleAfterLeave,
  handleConfirm
} = useCancelTaskDrawer();
</script>

<template>
  <v-dialog
    class="cancel-task-drawer"
    v-model="isDrawerOpen"
    @keydown.escape="closeDialog"
    @after-leave="handleAfterLeave"
    aria-labelledby="cancel-task-title"
    transition="dialog-bottom-transition"
    data-testid="circe-cancel-task-drawer"
    fullscreen
    style="top: 5%">
    <v-card class="d-flex flex-column">
      <v-card-title
        class="d-flex flex-column align-center gap-3 px-5 py-8 border-b border-color-line-border">
        <v-icon icon="mdi-close-circle" color="error" size="24"></v-icon>
        <h2 id="cancel-task-title" class="typography-heading-md-bold typography-md-heading-lg-bold">
          {{ t('yard.drawers.cancelTaskDrawer.title') }}
        </h2>
        <p
          v-if="subtitle"
          class="typography-body-md text--color-text-secondary text-center text-wrap"
          data-testid="circe-cancel-task-drawer-subtitle">
          {{ subtitle }}
        </p>
      </v-card-title>

      <v-card-text class="py-6 px-5 d-flex flex-column flex-grow-1">
        <p class="typography-body-md-bold mb-6 text-center text--color-text-secondary">
          {{ t('yard.drawers.cancelTaskDrawer.labels.includeAReason') }}
        </p>

        <div class="d-flex flex-column gap-2">
          <v-radio-group v-model="selectedReason" hide-details class="mt-0">
            <v-radio
              v-for="option in reasonOptions"
              :key="option"
              :value="option"
              :label="option"
              class="cancel-task-radio"></v-radio>
            <v-radio
              :value="OTHER_REASON"
              :label="t('yard.drawers.cancelTaskDrawer.labels.other')"
              class="cancel-task-radio"
              data-testid="circe-cancel-task-drawer-other-option"></v-radio>
          </v-radio-group>

          <v-textarea
            v-if="isOtherReason"
            v-model="customReason"
            :placeholder="t('yard.drawers.cancelTaskDrawer.placeholders.typeAReason')"
            color="primary"
            variant="outlined"
            density="compact"
            rows="3"
            data-testid="circe-cancel-task-drawer-custom-reason"
            hide-details></v-textarea>
        </div>
      </v-card-text>

      <v-card-actions
        class="d-flex flex-column gap-3 gap-md-4 align-stretch flex-md-row pa-4 border-t border-color-line-border">
        <v-btn
          variant="elevated"
          color="primary"
          size="large"
          class="flex-fill typography-button-lg order-1 order-md-2"
          data-testid="circe-cancel-task-drawer-confirm"
          :disabled="!canConfirm || isLoading"
          :loading="isLoading"
          @click="handleConfirm">
          {{ t('yard.drawers.cancelTaskDrawer.buttons.yesCancel') }}
        </v-btn>
        <v-btn
          variant="outlined"
          size="large"
          class="flex-fill typography-button-lg order-2 order-md-1"
          data-testid="circe-cancel-task-drawer-nevermind"
          :disabled="isLoading"
          @click="closeDialog">
          {{ t('yard.drawers.cancelTaskDrawer.buttons.nevermind') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
:deep(.cancel-task-radio) {
  .v-radio {
    align-items: flex-start;
  }

  .v-label {
    padding-left: 8px;
    white-space: normal;
    line-height: 1.4;
    opacity: 1;
  }
}

:deep(.v-radio-group) {
  .v-radio {
    background-color: $color-neutral-10;
    border-radius: 4px;
    padding: $spacing-3;
    margin-bottom: $spacing-2;
    border: 1px solid $color-line-border;
    min-height: 48px;
    display: flex;
    align-items: center;
    position: relative;
    cursor: pointer;
  }

  // Stretching the label's hit area over the whole card to make the row clickable.
  .v-label::before {
    content: '';
    position: absolute;
    inset: 0;
    cursor: pointer;
  }
}
</style>
